import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

// Resemble Enhance pricing: ~$0.05 per minute of audio.
const COST_PER_SECOND_MICRODOLLARS = (0.05 / 60) * 1_000_000;

const replicateResembleEnhanceParams = z
  .object({
    solver: z.enum(["RK4", "midpoint", "euler"]).optional(),
    nfe: z.number().int().min(1).max(256).optional(),
    chunkSeconds: z.number().min(1).max(60).optional(),
    chunksOverlap: z.number().min(0).max(10).optional(),
    denoise: z.boolean().optional(),
    attachments: z.array(attachment).optional(),
  })
  .strip();

export const replicateResembleEnhance = defineModel({
  key: "replicate-resemble-enhance",
  stepType: "audio-enhance",
  spec: {
    label: "Resemble Enhance",
    description:
      "Denoise and dereverberate speech audio using Resemble Enhance on Replicate.",
    icon: "replicate-icon.svg",
    tags: ["replicate", "audio-enhance"],
    features: { attachments: true } as const,
    options: {
      attachments: [
        {
          type: "media",
          label: "Audio or Video",
          slot: "media",
          required: true,
          allowedFormats: [
            "audio/wav",
            "audio/mp3",
            "audio/mpeg",
            "audio/m4a",
            "audio/ogg",
            "video/mp4",
            "video/quicktime",
            "video/webm",
          ],
        },
      ],
    },
    params: replicateResembleEnhanceParams,
  },
  providers: [
    {
      provider: "replicate",
      modelId: "resemble-ai/resemble-enhance",
      estimateCost(_params) {
        const estimatedDurationSeconds = 60;
        return Math.round(
          estimatedDurationSeconds *
            COST_PER_SECOND_MICRODOLLARS *
            (1 + BRAND_MARKUP),
        );
      },
      toPayload(params) {
        return {
          solver: params.solver,
          nfe: params.nfe,
          chunkSeconds: params.chunkSeconds,
          chunksOverlap: params.chunksOverlap,
          denoise: params.denoise,
        };
      },
    },
  ],
});
